import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useRouter from "common/hooks/use-router";
import useAuth from "common/hooks/useAuth";

import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { ObservationGroup } from "types/ObservationGroup";
import AlertsContext from "common/providers/alerts";
import usePark from "common/hooks/usePark";
import { FiEye } from "react-icons/fi";
import AnimalSelect from "components/select/AnimalSelect/AnimalSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import ObservationGroupItem from "./ObservationGroupItem";

function ObservationGroups() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();
  const canManageObservationGroups =
    (currentUser?.isSuperadmin ||
      currentUser?.isAdmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["observation-group"]
          ?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<ObservationGroup>("observation-groups", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    defaultSort: "name:ASC",
    cachePrefix: "ObservationGroups_all",
  });
  const { removeItem } = useItem<ObservationGroup>("observation-groups", "");

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/observation-groups/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "w-1/2",
      },
      {
        Header: t("forms.animal"),
        filterName: "animals",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <AnimalSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("forms.tag"),
        filterName: "tags",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <TagSelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.observation-groups")}
        subtitle={t("titleLegends.observationGroups")}
        btnTitle={t("newItem.observationGroup")}
        btnSubtitle={t("newItem.observationGroupLegend")}
        Icon={FiEye}
        addLink="/observation-groups/add"
        addLinkDisabled={!canManageObservationGroups}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={ObservationGroupItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default ObservationGroups;
